<script setup lang="ts">
/**
 * One command of an office ribbon (Write, Calc).
 *
 * - `size="large"` (default): the icon over its label — „Einfügen“, „Tabelle“,
 *   „Drucken“ — at the ribbon's big-action height. A long label wraps onto a
 *   second line at `--office-ribbon-big-label-max` instead of widening the
 *   button.
 * - `size="small"`: a square icon command at the ribbon's control height —
 *   „Fett“, „Linksbündig“. The label is its accessible name and tooltip;
 *   `show-label` puts it beside the icon instead („Kopieren“, „Zeile
 *   einfügen“).
 *
 * The icon goes into the `icon` slot (`NxOfficeIcon`, `size="lg"` on a large
 * command). `pressed` makes it a toggle (`aria-pressed` and the ribbon's active
 * look); a host that opens a menu or popover from it sets `aria-expanded`, and
 * an open one shows the same look. `caret` marks such a trigger: after the
 * label on a large command, in the corner of a small one, so the square keeps
 * its size. Every other attribute and listener goes to the `<button>`.
 *
 * Inside `NxOfficeRibbon` the look follows the ribbon (`--office-ribbon-*`);
 * outside one it falls back to the plain tokens.
 */
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';
import { computed, useTemplateRef } from 'vue';
import '../office-ribbon.css';

export type NxRibbonButtonSize = 'large' | 'small';

const props = withDefaults(
  defineProps<{
    /** The command's name: shown on a large command (and with `show-label`), else its accessible name. */
    label: string;
    size?: NxRibbonButtonSize;
    /** Small commands only: the label beside the icon. */
    showLabel?: boolean;
    /** Tooltip; an icon-only command defaults to its label. */
    title?: string;
    /** Toggle state: sets `aria-pressed` and the active look. Unset leaves a plain command. */
    pressed?: boolean;
    disabled?: boolean;
    /** Marks a menu or popover trigger. */
    caret?: boolean;
  }>(),
  {
    size: 'large',
    showLabel: false,
    title: undefined,
    pressed: undefined,
    disabled: false,
    caret: false,
  }
);

const labelShown = computed(() => props.size === 'large' || props.showLabel);

const button = useTemplateRef<HTMLButtonElement>('button');

defineExpose({
  /** The `<button>`, for a menu or popover to anchor to. */
  element: button,
  focus: (options?: FocusOptions) => button.value?.focus(options),
});
</script>

<template>
  <button
    ref="button"
    type="button"
    class="nx-ribbon-button"
    :class="[
      `nx-ribbon-button--${props.size}`,
      { 'has-label': labelShown, 'has-caret': props.caret, 'is-pressed': props.pressed === true },
    ]"
    :title="props.title ?? (labelShown ? undefined : props.label)"
    :aria-label="labelShown ? undefined : props.label"
    :aria-pressed="props.pressed === undefined ? undefined : props.pressed"
    :disabled="props.disabled"
  >
    <span v-if="$slots.icon" class="nx-ribbon-button__icon"><slot name="icon" /></span>
    <span v-if="labelShown" class="nx-ribbon-button__label">
      {{ props.label }}
      <ChevronDown
        v-if="props.caret"
        class="nx-ribbon-button__caret nxt-icon-2xs"
        aria-hidden="true"
      />
    </span>
    <ChevronDown
      v-else-if="props.caret"
      class="nx-ribbon-button__caret nxt-icon-2xs"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped>
.nx-ribbon-button {
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  margin: 0;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--office-ribbon-fg, var(--nxt-fg));
  font-family: var(--office-ribbon-font-family, var(--nxt-font));
  font-size: var(--office-ribbon-font-size, calc(var(--nxt-size-unit) * 0.8125));
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-control);
  white-space: nowrap;
  cursor: pointer;
}
.nx-ribbon-button__icon {
  display: inline-flex;
  flex: none;
}

/* Large: icon over label, the ribbon's big-action box. */
.nx-ribbon-button--large {
  flex-direction: column;
  gap: calc(var(--nxt-size-unit) * 0.1875);
  min-width: calc(var(--nxt-size-unit) * 3.25);
  height: var(--office-ribbon-big-action-height, var(--nxt-tile-size-xl));
  padding: calc(var(--nxt-size-unit) * 0.1875) calc(var(--nxt-size-unit) * 0.375);
}
.nx-ribbon-button--large .nx-ribbon-button__label {
  /* Two short lines rather than one long one: a label like „Vorlagen und
     Textbausteine“ otherwise makes its button the widest thing in the ribbon. */
  max-width: var(--office-ribbon-big-label-max, calc(var(--nxt-size-unit) * 4.25));
  /* Every label takes two lines' room, a one-line label centred in it: the
     content keeps one height, so each icon sits at the same height in the
     centred button whether its label wraps or not (a wrapping label used to
     push its icon up), and a one-line label leaves no gap below it (I-3178). */
  display: grid;
  align-content: center;
  min-block-size: 2lh;
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  line-height: var(--nxt-line-height-tight);
  text-align: center;
  white-space: normal;
  hyphens: auto;
  /* Only a genuinely long word is split — „Seitenum-bruch“ reads worse than a
     label that is a few pixels wider. */
  hyphenate-limit-chars: 15 6 6;
}

/* Small: a square at the ribbon's control height; with its label, as wide as
   icon and label need. */
.nx-ribbon-button--small {
  gap: var(--nxt-space-xs);
  min-width: var(--office-ribbon-control-height, calc(var(--nxt-size-unit) * 1.75));
  height: var(--office-ribbon-control-height, calc(var(--nxt-size-unit) * 1.75));
  padding: 0;
}
.nx-ribbon-button--small.has-label {
  padding-inline: var(--nxt-space-xs);
}

.nx-ribbon-button__caret {
  flex: none;
  color: var(--nxt-fg-muted);
  vertical-align: middle;
}
.nx-ribbon-button--large .nx-ribbon-button__caret {
  margin-inline-start: calc(var(--nxt-hairline) * 2);
}
/* A small icon-only trigger keeps its square: the caret sits in the corner. */
.nx-ribbon-button--small:not(.has-label) > .nx-ribbon-button__caret {
  position: absolute;
  inset-block-end: calc(var(--nxt-hairline) * 2);
  inset-inline-end: calc(var(--nxt-hairline) * 2);
}
.nx-ribbon-button--large.has-caret .nx-ribbon-button__label {
  /* The caret stays beside a one-line label, both centred in the two lines'
     room like any label. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.nx-ribbon-button:hover:not(:disabled) {
  background: var(--office-ribbon-hover-bg, var(--nxt-surface-sunken-bg));
  color: var(--office-ribbon-hover-fg, var(--nxt-brand-bg));
}
.nx-ribbon-button.is-pressed,
.nx-ribbon-button[aria-expanded='true'] {
  border-color: var(--office-ribbon-active-border, var(--nxt-brand-border));
  background: var(--office-ribbon-active-bg, var(--nxt-brand-subtle-bg));
  color: var(--office-ribbon-hover-fg, var(--nxt-brand-bg));
}
.nx-ribbon-button:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-ribbon-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
